import { useEffect, useState } from "react";
import { Instagram, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { SITE } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InstagramFeed = ({ embedded = false }) => {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/instagram/profile-embed`)
      .then((r) => {
        if (!r.ok) throw new Error("embed failed");
        return r.json();
      })
      .then((d) => {
        if (!d.html) throw new Error("no html");
        setHtml(d.html);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (!html) return;
    if (window.instgrm) {
      window.instgrm.Embeds.process();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.instagram.com/embed.js";
    s.async = true;
    s.onload = () => window.instgrm?.Embeds.process();
    document.body.appendChild(s);
  }, [html]);

  const body = (
    <div className="grid items-start gap-8 lg:grid-cols-12">
      <div className="min-w-0 lg:col-span-5">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E1306C]">Instagram</p>
        <h3 className="mt-3 font-display text-2xl font-medium tracking-tight text-cream sm:text-3xl">
          Fresh reels from the floor
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-fog">
          New arrivals, demos and walkthroughs — this feed updates itself every time we post.
        </p>
        <a
          href={SITE.instagram}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="instagram-follow-button"
          className="group mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-cream transition-all duration-300 hover:border-[#E1306C]/60 hover:text-[#E1306C]"
        >
          <Instagram size={15} className="text-[#E1306C]" />
          Follow {SITE.instagramHandle}
          <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
      <div className="min-w-0 lg:col-span-7 lg:col-start-6">
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/95 p-1 sm:p-2">
          {error ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-2xl bg-steel p-8 text-center">
              <Instagram size={28} className="text-[#E1306C]" />
              <p className="text-sm text-fog">
                The live feed couldn't load here — see our latest reels directly on Instagram.
              </p>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-fallback-link"
                className="inline-flex items-center gap-2 rounded-full border border-[#E1306C]/40 px-5 py-2.5 text-sm text-[#E1306C] transition-colors hover:bg-[#E1306C] hover:text-white"
              >
                Open {SITE.instagramHandle} <ArrowUpRight size={14} />
              </a>
            </div>
          ) : html ? (
            <div
              className="instagram-embed-fix flex w-full min-w-0 justify-center overflow-hidden [&>blockquote]:!my-0"
              data-testid="instagram-embed"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <div className="flex min-h-[420px] w-full animate-pulse items-center justify-center rounded-2xl bg-steel/60">
              <Instagram size={28} className="text-ash" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <Reveal>
        <div
          className="rounded-3xl border border-[#E1306C]/25 bg-[#E1306C]/[0.04] p-4 sm:p-10"
          data-testid="instagram-section"
        >
          {body}
        </div>
      </Reveal>
    );
  }

  return (
    <section className="border-t border-white/10 py-24 sm:py-32" data-testid="instagram-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{body}</div>
    </section>
  );
};
